import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Search, Download, FileSpreadsheet, MapPin, AlertCircle, CheckCircle2, Save, Trash2, Layers, Eye, X, Mail, Send, Pause, Play, Inbox, MousePointerClick, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import AdminLayout from '@/components/admin/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MAIL_STATUSES = [
  { value: 'queued', label: 'Queued', color: 'text-neutral-300 bg-neutral-700/50' },
  { value: 'sent', label: 'Sent', color: 'text-blue-300 bg-blue-500/15 border border-blue-500/40' },
  { value: 'opened', label: 'Opened', color: 'text-cyan-300 bg-cyan-500/15 border border-cyan-500/40' },
  { value: 'responded', label: 'Responded', color: 'text-yellow-300 bg-yellow-500/15 border border-yellow-500/40' },
  { value: 'converted', label: 'Converted ✓', color: 'text-green-300 bg-green-500/15 border border-green-500/40' },
  { value: 'no_response', label: 'No response', color: 'text-neutral-400 bg-neutral-800/60' },
  { value: 'bounced', label: 'Bounced', color: 'text-red-300 bg-red-500/15 border border-red-500/40' },
  { value: 'unsubscribed', label: 'Unsubscribed', color: 'text-red-400 bg-red-500/15 border border-red-500/40' },
];

/**
 * Admin Lead Finder — Google Places API (New) backed prospecting tool.
 *
 * Workflow:
 *   1. Enter 1-10 ZIP codes (comma separated)
 *   2. Pick an industry preset ("All SMBs" by default)
 *   3. (Optional) Tick cohorts to exclude — already-mailed businesses won't reappear
 *   4. Hit Search — backend runs ~N nearby searches and returns deduped rows
 *   5. Review/select rows in the table
 *   6. Download either ONE combined xlsx or a .zip of per-ZIP files,
 *      OR save the selected rows as a Prospect Cohort for future dedup.
 *
 * Google Places spend goes to whatever GCP project owns
 * GOOGLE_PLACES_API_KEY (NOT theailedger).
 */
function authHeaders() {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
}

export default function AdminLeadFinder() {
  const [zipsText, setZipsText] = useState('98371, 98372, 98373, 98375');
  const [industry, setIndustry] = useState('all_smb');
  const [industries, setIndustries] = useState([
    { value: 'all_smb', label: 'All SMBs (broad)' },
  ]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null); // 'combined' | 'by_zip' | null
  const [savingCohort, setSavingCohort] = useState(false);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [cohorts, setCohorts] = useState([]);
  const [excludeCohortIds, setExcludeCohortIds] = useState(new Set());
  const [cohortName, setCohortName] = useState('');
  const [cohortNotes, setCohortNotes] = useState('');
  const [openCohort, setOpenCohort] = useState(null); // full cohort doc when modal is open
  const [openCohortLoading, setOpenCohortLoading] = useState(false);

  const openCohortDetails = async (cohortId) => {
    setOpenCohortLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/lead-finder/cohorts/${cohortId}`,
        { headers: authHeaders() }
      );
      setOpenCohort(res.data);
    } catch {
      toast.error('Could not load cohort.');
    } finally {
      setOpenCohortLoading(false);
    }
  };

  const updateBusinessStatus = async (cohortId, placeId, status) => {
    try {
      const res = await axios.patch(
        `${API_URL}/api/admin/lead-finder/cohorts/${cohortId}/status`,
        { place_id: placeId, status },
        { headers: authHeaders() }
      );
      // Locally update the modal state without re-fetching the whole doc
      setOpenCohort((prev) => {
        if (!prev || prev.cohort_id !== cohortId) return prev;
        const businesses = prev.businesses.map((b) =>
          b.place_id === placeId
            ? { ...b, mail_status: status, mail_status_updated_at: new Date().toISOString() }
            : b
        );
        return { ...prev, businesses, status_counts: res.data.status_counts };
      });
      // Refresh the cohort list summary
      loadCohorts();
    } catch {
      toast.error('Status update failed.');
    }
  };

  const [enrichingCohortId, setEnrichingCohortId] = useState(null);

  // ── Campaign composer state ───────────────────────────────────────────
  const [campaignCohortId, setCampaignCohortId] = useState(null); // open modal for cohort
  const [campaign, setCampaign] = useState({
    subject_a: 'AI tools your {{city}} competitors are using',
    subject_b: 'Most {{city}} businesses are missing this',
    body_template: `Hi {{business_name}},

I'm Jake Colson, Founder of theailedger.ai — a curated marketplace where business owners discover AI tools that actually work for SMBs (no enterprise-only fluff).

A few things our buyers have automated this month:

- Customer inquiries answered 24/7 — never miss a call again, even on weekends
- Social posts + product photos generated in minutes instead of paying $200/month for a freelancer
- Local SEO content that gets your business found on Google maps before competitors
- Booking + appointment reminders that cut no-shows by 40%
- Customer review responses drafted automatically in your tone

No need to learn 50 tools or sign up for everything. We've already vetted what works for businesses like {{business_name}}.

Worth a 2-minute look? https://theailedger.ai

Either way, hope business is good in {{city}}.

— Jake`,
    from_name: 'Jake Colson',
    reply_to: '',
    daily_cap: 25,
  });
  const [campaignPreview, setCampaignPreview] = useState(null);
  const [campaignSending, setCampaignSending] = useState(false);

  // Auto-load the saved global outreach default on mount. Lets the admin
  // edit one template once (via /outreach-template/default) and have it
  // pre-fill every future cohort's Launch dialog. Silent on miss — falls
  // back to the hardcoded starter copy above.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/api/admin/lead-finder/outreach-template/default`,
          { headers: authHeaders() }
        );
        const d = res.data || {};
        if (cancelled || !d.subject_a || !d.body_template) return;
        setCampaign((c) => ({
          ...c,
          subject_a: d.subject_a,
          subject_b: d.subject_b || c.subject_b,
          body_template: d.body_template,
          from_name: d.from_name || c.from_name,
          daily_cap: d.daily_cap || c.daily_cap,
        }));
      } catch (e) {
        // Non-fatal — admin can still type a template manually.
        console.debug('outreach default template not loaded:', e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Campaigns tab state ───────────────────────────────────────────────
  // Tab toggle drives which top-level panel renders: 'search' (the
  // historical default — search form + cohorts list) or 'campaigns'
  // (the new visibility surface). Persisted to localStorage so reload
  // doesn't bounce the admin back to Search on every navigation.
  const [activeTab, setActiveTab] = useState(() => {
    try { return localStorage.getItem('lead_finder_tab') || 'search'; }
    catch { return 'search'; }
  });
  const [campaignsList, setCampaignsList] = useState([]);
  // Controls which campaigns the totals tile aggregates. Defaults to
  // 'active' so the founder sees the CURRENT campaign's stats first —
  // lifetime totals are misleading when an older campaign had broken
  // tracking that's since been fixed (e.g. the 247/0% red herring).
  const [statsScope, setStatsScope] = useState('active');
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignActionId, setCampaignActionId] = useState(null);

  // ── Cohort autopilot diagnostic ─────────────────────────────────────────
  // Surfaces cohorts where leads exist but no campaign is scheduled
  // (i.e. the autopilot will never email them). Built after a one-off
  // manual send pattern left a list of zipcode leads stuck with no
  // campaign_id attached (June 3, 2026).
  const [diagnostic, setDiagnostic] = useState([]);
  const [diagnosticLoading, setDiagnosticLoading] = useState(false);
  const [resumeModal, setResumeModal] = useState(null); // cohort row or null
  const [resumeForm, setResumeForm] = useState({
    subject_a: '',
    subject_b: '',
    body_template: '',
    daily_cap: 25,
    from_name: 'Jake Colson',
  });
  const [resumeSubmitting, setResumeSubmitting] = useState(false);

  // Edit-campaign modal — allows the founder to change subject_a,
  // subject_b, body_template, and daily_cap on an existing campaign
  // WITHOUT recreating it. Any recipient still `queued`/`paused` picks
  // up the new copy on the next send tick. Already-sent recipients keep
  // whatever they got — we don't rewrite history.
  const [editCampaign, setEditCampaign] = useState(null); // full campaign obj or null
  const [editForm, setEditForm] = useState({
    subject_a: '',
    subject_b: '',
    body_template: '',
    daily_cap: 25,
  });
  const [editSubmitting, setEditSubmitting] = useState(false);

  const openEditCampaign = (camp) => {
    setEditCampaign(camp);
    setEditForm({
      subject_a: camp.subject_a || '',
      subject_b: camp.subject_b || '',
      body_template: camp.body_template || '',
      daily_cap: camp.daily_cap || 25,
    });
  };

  const submitEditCampaign = async () => {
    if (!editCampaign) return;
    if (!editForm.subject_a.trim() || !editForm.body_template.trim()) {
      toast.error('Subject A and body template are required');
      return;
    }
    setEditSubmitting(true);
    try {
      await axios.patch(
        `${API_URL}/api/admin/lead-finder/campaigns/${editCampaign.campaign_id}`,
        {
          subject_a: editForm.subject_a.trim(),
          subject_b: editForm.subject_b.trim() || null,
          body_template: editForm.body_template.trim(),
          daily_cap: Number(editForm.daily_cap) || 25,
        },
        { headers: authHeaders() }
      );
      toast.success('Campaign updated. New copy applies to next send tick.');
      setEditCampaign(null);
      loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update campaign');
    } finally {
      setEditSubmitting(false);
    }
  };

  // Mark-as-contacted (for leads who got pre-cohort manual blasts)
  const [markModal, setMarkModal] = useState(null); // cohort or null
  // Webhook log modal — pulls /api/admin/resend-events/recent to show
  // every event Resend has fired at our endpoint. Useful when "test
  // clicks aren't registering" — lets the founder see if Resend ever
  // sent `email.clicked` at all (config issue) vs whether we received
  // it but routed it wrong (code issue).
  const [showWebhookLog, setShowWebhookLog] = useState(false);
  const [webhookEvents, setWebhookEvents] = useState(null);
  const [webhookLoading, setWebhookLoading] = useState(false);

  const loadWebhookEvents = async () => {
    setWebhookLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/resend-events/recent?limit=50`,
        { headers: authHeaders() }
      );
      setWebhookEvents(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load webhook events');
    } finally {
      setWebhookLoading(false);
    }
  };

  useEffect(() => {
    if (showWebhookLog) loadWebhookEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWebhookLog]);
  const [markEmails, setMarkEmails] = useState('');
  const [markSubmitting, setMarkSubmitting] = useState(false);

  const submitMarkContacted = async () => {
    if (!markModal) return;
    const emails = markEmails
      .split(/[\s,;\n]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && e.includes('@'));
    if (emails.length === 0) {
      toast.error('Paste at least one email');
      return;
    }
    setMarkSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${markModal.cohort_id}/mark-contacted`,
        { emails, status: 'sent' },
        { headers: authHeaders() }
      );
      const d = res.data || {};
      toast.success(`Marked ${d.marked} as already sent${d.not_in_cohort_count ? ` · ${d.not_in_cohort_count} not in this cohort` : ''}.`);
      setMarkModal(null);
      setMarkEmails('');
      await loadDiagnostic();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mark failed');
    } finally {
      setMarkSubmitting(false);
    }
  };

  const loadDiagnostic = useCallback(async () => {
    setDiagnosticLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/lead-finder/cohorts-diagnostic`,
        { headers: authHeaders() }
      );
      setDiagnostic(res.data.cohorts || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load cohort health');
    } finally {
      setDiagnosticLoading(false);
    }
  }, []);

  const openResumeModal = (cohort) => {
    setResumeForm({
      subject_a: '',
      subject_b: '',
      body_template: '',
      daily_cap: 25,
      from_name: 'Jake Colson',
    });
    setResumeModal(cohort);
  };

  // ── One-click resume — uses last template from this cohort, or the
  // saved global default. Only opens the manual modal if the backend
  // can't find any template anywhere (HTTP 400). True autopilot.
  const oneClickResume = async (cohort) => {
    if (!cohort) return;
    setCampaignActionId(cohort.cohort_id);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${cohort.cohort_id}/resume`,
        {},  // empty body — backend picks up last template or global default
        { headers: authHeaders() }
      );
      const d = res.data || {};
      if (d.resumed) {
        const sourceLabel =
          d.template_source === 'global_default' ? 'global default template' :
          d.template_source && d.template_source.startsWith('reused_from_') ? "this cohort's last template" :
          'provided template';
        toast.success(
          `Autopilot resumed — ${d.queued_count} leads queued (${sourceLabel}, ~${d.estimated_days} day${d.estimated_days === 1 ? '' : 's'} at ${cohort.stuck_no_campaign && d.estimated_days ? Math.round(cohort.stuck_no_campaign / d.estimated_days) : 25}/day).`,
          { duration: 6000 }
        );
        await Promise.all([loadDiagnostic(), loadCampaigns()]);
      } else {
        toast.message(`Nothing to resume — ${d.reason || 'no stuck leads'}.`);
      }
    } catch (e) {
      // HTTP 400 = no template found anywhere → fall back to the manual modal
      if (e?.response?.status === 400) {
        toast.message('No saved template yet — please fill in below for this first send.');
        openResumeModal(cohort);
      } else {
        toast.error(e?.response?.data?.detail || 'Resume failed');
      }
    } finally {
      setCampaignActionId(null);
    }
  };

  const submitResume = async () => {
    if (!resumeModal) return;
    if (!resumeForm.subject_a.trim() || !resumeForm.body_template.trim()) {
      toast.error('Subject A and body are required.');
      return;
    }
    setResumeSubmitting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${resumeModal.cohort_id}/resume`,
        {
          subject_a: resumeForm.subject_a.trim(),
          subject_b: resumeForm.subject_b.trim() || null,
          body_template: resumeForm.body_template.trim(),
          daily_cap: Number(resumeForm.daily_cap) || 25,
          from_name: resumeForm.from_name.trim() || 'Jake Colson',
        },
        { headers: authHeaders() }
      );
      const d = res.data || {};
      if (d.resumed) {
        toast.success(
          `Autopilot resumed — ${d.queued_count} leads queued over ~${d.estimated_days} day${d.estimated_days === 1 ? '' : 's'}.`
        );
        setResumeModal(null);
        await Promise.all([loadDiagnostic(), loadCampaigns()]);
      } else {
        toast.message(`Nothing to resume — ${d.reason || 'no stuck leads found'}.`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed');
    } finally {
      setResumeSubmitting(false);
    }
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    try { localStorage.setItem('lead_finder_tab', tab); } catch { /* noop */ }
  };

  const loadCampaigns = useCallback(async () => {
    setCampaignsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/admin/lead-finder/campaigns`,
        { headers: authHeaders() }
      );
      setCampaignsList(res.data.campaigns || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load campaigns');
    } finally {
      setCampaignsLoading(false);
    }
  }, []);

  // Auto-load whenever the Campaigns tab becomes active
  useEffect(() => {
    if (activeTab === 'campaigns') {
      loadCampaigns();
      loadDiagnostic();
    }
  }, [activeTab, loadCampaigns, loadDiagnostic]);

  // Poll campaigns every 20s while the Campaigns tab is open so the
  // "Test click received" badge appears within seconds of clicking a
  // link in a test email — without needing a manual page refresh.
  // Cheap (one GET, ~1KB) and only runs while the tab is foregrounded.
  useEffect(() => {
    if (activeTab !== 'campaigns') return;
    const id = setInterval(() => {
      if (document.visibilityState === 'visible') loadCampaigns();
    }, 20000);
    return () => clearInterval(id);
  }, [activeTab, loadCampaigns]);

  const pauseCampaign = async (cid) => {
    if (!window.confirm('Pause this campaign? Queued emails will stop firing until you resume.')) return;
    setCampaignActionId(cid);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/campaigns/${cid}/pause`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Paused ${res.data.paused} recipients`);
      await loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pause failed');
    } finally {
      setCampaignActionId(null);
    }
  };

  const resumeCampaign = async (cid) => {
    setCampaignActionId(cid);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/campaigns/${cid}/resume`,
        {},
        { headers: authHeaders() }
      );
      toast.success(`Resumed ${res.data.resumed} recipients`);
      await loadCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resume failed');
    } finally {
      setCampaignActionId(null);
    }
  };

  const testSendCampaign = async (cid) => {
    // Preview the cold email in my own inbox before blasting the cohort —
    // same From / Reply-To / tags as the real cron, so it's an apples-to-
    // apples deliverability + rendering test.
    const target = window.prompt(
      'Send a test of variant A to which email?\n\n(Gmail recommended — Primary vs Promotions vs Spam is the most useful signal.)',
      ''
    );
    if (!target) return;
    setCampaignActionId(cid);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/campaigns/${cid}/test-send`,
        { to: target },
        { headers: authHeaders() }
      );
      toast.success(`Test sent → ${res.data.to} (from ${res.data.from})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally {
      setCampaignActionId(null);
    }
  };

  const openCampaignModal = (cohortId) => {
    setCampaignCohortId(cohortId);
    setCampaignPreview(null);
  };
  const closeCampaignModal = () => {
    setCampaignCohortId(null);
    setCampaignPreview(null);
  };

  const runPreview = async () => {
    if (!campaignCohortId) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${campaignCohortId}/campaign/preview`,
        campaign,
        { headers: authHeaders() }
      );
      setCampaignPreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed.');
    }
  };

  const launchCampaign = async () => {
    if (!campaignCohortId) return;
    if (!window.confirm(
      `Launch this campaign?\n\nEmails will go out throttled at ${campaign.daily_cap}/day. ` +
      `Each recipient gets ONE email. Variants A/B split 50/50 for testing. ` +
      `Compliance footer (address + unsubscribe link) is added automatically.`
    )) return;
    setCampaignSending(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${campaignCohortId}/campaign`,
        campaign,
        { headers: authHeaders() }
      );
      const suppressed = res.data.suppressed_already_contacted || 0;
      const suppressMsg = suppressed > 0
        ? ` (${suppressed} already-contacted businesses auto-excluded ✓)`
        : '';
      toast.success(
        `Scheduled ${res.data.eligible_count} sends over ${res.data.estimated_days} day(s). ` +
        `A: ${res.data.variant_a_count} / B: ${res.data.variant_b_count}${suppressMsg}`
      );
      closeCampaignModal();
      // Refresh the cohort modal to show new variant assignments
      if (openCohort?.cohort_id === campaignCohortId) {
        await openCohortDetails(campaignCohortId);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Launch failed.');
    } finally {
      setCampaignSending(false);
    }
  };

  const enrichEmails = async (cohortId) => {
    if (!window.confirm(
      'Scrape each business website for a public contact email?\n\n' +
      'Realistic hit rate: 35-50%. Runs in the BACKGROUND — you can close ' +
      'this dialog and check back in 1-3 minutes (refresh the cohort to see emails).'
    )) return;

    setEnrichingCohortId(cohortId);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts/${cohortId}/enrich-emails`,
        {},
        { headers: authHeaders() }  // no long timeout needed — backend kicks off background task
      );
      const d = res.data || {};
      if (d.status === 'running') {
        toast.success(
          `Enrichment running for ${d.queued} websites — refresh in 1-3 min to see results.`,
          { duration: 6000 }
        );
      } else if (d.status === 'already_complete') {
        toast.message(d.message || 'All websites already enriched.');
      } else if (d.status === 'no_businesses') {
        toast.message('This cohort has no businesses yet.');
      } else {
        toast.success(`Enrichment complete: ${d.found || 0}/${d.attempted || 0}`);
      }
      // Poll once after 90 seconds to refresh modal data automatically
      setTimeout(() => { openCohortDetails(cohortId); }, 90000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email enrichment failed to start.');
    } finally {
      setEnrichingCohortId(null);
    }
  };

  const loadCohorts = useCallback(() => {
    axios
      .get(`${API_URL}/api/admin/lead-finder/cohorts`, { headers: authHeaders() })
      .then((r) => setCohorts(r.data.cohorts || []))
      .catch(() => { /* silently ignore */ });
  }, []);

  useEffect(() => {
    axios
      .get(`${API_URL}/api/admin/lead-finder/industries`, { headers: authHeaders() })
      .then((r) => setIndustries(r.data.industries || []))
      .catch(() => { /* fallback default already set */ });
    loadCohorts();
  }, [loadCohorts]);

  const parseZips = () =>
    zipsText
      .split(/[\s,]+/)
      .map((z) => z.trim())
      .filter((z) => /^\d{5}$/.test(z));

  const runSearch = async () => {
    const zips = parseZips();
    if (zips.length === 0) {
      toast.error('Enter at least one valid 5-digit ZIP code.');
      return;
    }
    if (zips.length > 10) {
      toast.error('Maximum 10 ZIP codes per search.');
      return;
    }
    setLoading(true);
    setResults([]);
    setMeta(null);
    setSelected(new Set());
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/search`,
        {
          zip_codes: zips,
          industry,
          require_address: true,
          exclude_cohort_ids: Array.from(excludeCohortIds),
        },
        { headers: authHeaders() }
      );
      setResults(res.data.businesses || []);
      setMeta({
        total: res.data.total,
        emails_found: res.data.emails_found || 0,
        counts_by_zip: res.data.counts_by_zip || {},
        zip_codes_resolved: res.data.zip_codes_resolved || [],
        zip_codes_unresolved: res.data.zip_codes_unresolved || [],
      });
      // Pre-select all rows that have an email — those are the only ones
      // the campaign autopilot can actually mail. Rows without emails get
      // saved to the cohort too (for the manual enrichment retry path),
      // but they're unchecked by default so the admin doesn't accidentally
      // queue a campaign with 50% blanks.
      setSelected(new Set((res.data.businesses || []).filter((b) => b.email).map((b) => b.place_id)));
      const found = res.data.emails_found || 0;
      const total = res.data.total || 0;
      toast.success(`Found ${total} businesses · ${found} with email (${total > 0 ? Math.round((found / total) * 100) : 0}%)`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Search failed.';
      toast.error(typeof msg === 'string' ? msg : 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelected = (place_id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(place_id)) next.delete(place_id);
      else next.add(place_id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === results.length) setSelected(new Set());
    else setSelected(new Set(results.map((b) => b.place_id)));
  };

  const downloadExport = async (mode) => {
    const chosen = results.filter((b) => selected.has(b.place_id));
    if (chosen.length === 0) {
      toast.error('Select at least one business to export.');
      return;
    }
    setExporting(mode);
    try {
      const url =
        mode === 'by_zip'
          ? `${API_URL}/api/admin/lead-finder/export-by-zip`
          : `${API_URL}/api/admin/lead-finder/export-xlsx`;
      const res = await axios.post(
        url,
        { businesses: chosen },
        { headers: authHeaders(), responseType: 'blob' }
      );
      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/octet-stream',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = mode === 'by_zip' ? 'leads_by_zip.zip' : 'leads_combined.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`Exported ${chosen.length} businesses.`);
    } catch (e) {
      toast.error('Export failed.');
    } finally {
      setExporting(null);
    }
  };

  const toggleExcludeCohort = (cohortId) => {
    setExcludeCohortIds((prev) => {
      const next = new Set(prev);
      if (next.has(cohortId)) next.delete(cohortId);
      else next.add(cohortId);
      return next;
    });
  };

  const saveAsCohort = async () => {
    const chosen = results.filter((b) => selected.has(b.place_id));
    if (chosen.length === 0) {
      toast.error('Select at least one business to save.');
      return;
    }
    if (!cohortName.trim() || cohortName.trim().length < 2) {
      toast.error('Give the cohort a name (e.g. "Puyallup SMB Mailing — Apr 2026").');
      return;
    }
    setSavingCohort(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/admin/lead-finder/cohorts`,
        {
          name: cohortName.trim(),
          notes: cohortNotes.trim(),
          zip_codes: parseZips(),
          industry,
          businesses: chosen,
        },
        { headers: authHeaders() }
      );
      toast.success(`Saved cohort "${res.data.name}" (${res.data.business_count} businesses).`);
      setCohortName('');
      setCohortNotes('');
      loadCohorts();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Save failed.';
      toast.error(typeof msg === 'string' ? msg : 'Save failed.');
    } finally {
      setSavingCohort(false);
    }
  };

  const deleteCohort = async (cohortId, name) => {
    if (!window.confirm(`Delete cohort "${name}"? Future searches will no longer auto-exclude its businesses.`)) {
      return;
    }
    try {
      await axios.delete(
        `${API_URL}/api/admin/lead-finder/cohorts/${cohortId}`,
        { headers: authHeaders() }
      );
      toast.success(`Deleted "${name}".`);
      setExcludeCohortIds((prev) => {
        const next = new Set(prev);
        next.delete(cohortId);
        return next;
      });
      loadCohorts();
    } catch {
      toast.error('Delete failed.');
    }
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6" data-testid="admin-lead-finder">
        <header>
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-2">
            <MapPin className="w-6 h-6 text-[#00F0FF]" />
            Lead Finder
          </h1>
          <p className="text-sm text-neutral-400">
            Find small/medium businesses by ZIP for direct mail or B2B outreach. Powered by Google Places.
            <span className="block mt-1 text-xs text-neutral-500">
              Cost: ~$0.03 per ZIP × industry-type. Free under Google's $200/mo Maps credit.
            </span>
          </p>
        </header>

        {/* Top-level tab toggle — Search & Cohorts vs Campaigns.
            Campaigns tab gives the founder visibility into what's
            actually scheduled to fire and what's already gone out,
            with one-click pause/resume per campaign. */}
        <div className="border-b border-white/10 flex gap-1" data-testid="lead-finder-tabs">
          <button
            type="button"
            onClick={() => switchTab('search')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === 'search'
                ? 'text-white border-[#00F0FF]'
                : 'text-neutral-400 border-transparent hover:text-neutral-200'
            }`}
            data-testid="tab-search"
          >
            <Search className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Search & Cohorts
          </button>
          <button
            type="button"
            onClick={() => switchTab('campaigns')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 transition ${
              activeTab === 'campaigns'
                ? 'text-white border-[#00F0FF]'
                : 'text-neutral-400 border-transparent hover:text-neutral-200'
            }`}
            data-testid="tab-campaigns"
          >
            <Inbox className="w-4 h-4 inline-block mr-1.5 -mt-0.5" />
            Campaigns
            {campaignsList.length > 0 && (
              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-neutral-300">
                {campaignsList.length}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'campaigns' && (
          <section className="space-y-3" data-testid="campaigns-panel">
            {/* Cross-campaign totals, scoped to whichever campaign-status
                bucket the founder picks. Default 'active' answers the
                question "how is my CURRENT campaign doing" — which is
                almost always what's wanted on this dashboard. Lifetime
                view is still available for trend analysis. */}
            {campaignsList.length > 0 && (() => {
              const isActive = (c) => {
                if (c.status === 'paused') return false;
                const sent = (c.stats?.a?.sent || 0) + (c.stats?.b?.sent || 0)
                           + (c.stats?.a?.responded || 0) + (c.stats?.b?.responded || 0);
                const total = (c.totals?.a?.rows || 0) + (c.totals?.b?.rows || 0);
                return !(sent >= total && total > 0);  // active = not complete, not paused
              };
              const scoped = statsScope === 'active'
                ? campaignsList.filter(isActive)
                : statsScope === 'completed'
                  ? campaignsList.filter((c) => !isActive(c) && c.status !== 'paused')
                  : campaignsList;
              const totals = scoped.reduce((acc, c) => {
                const aSent = (c.stats?.a?.sent || 0) + (c.stats?.a?.responded || 0);
                const bSent = (c.stats?.b?.sent || 0) + (c.stats?.b?.responded || 0);
                const aClicks = c.stats?.a?.clicks || 0;
                const bClicks = c.stats?.b?.clicks || 0;
                acc.sent += aSent + bSent;
                acc.clicks += aClicks + bClicks;
                acc.responded += (c.stats?.a?.responded || 0) + (c.stats?.b?.responded || 0);
                return acc;
              }, { sent: 0, clicks: 0, responded: 0 });
              const ctr = totals.sent > 0 ? Math.round((totals.clicks / totals.sent) * 100) : 0;
              const scopeLabel = statsScope === 'active'
                ? 'Active campaigns'
                : statsScope === 'completed'
                  ? 'Completed campaigns'
                  : 'All time (lifetime)';
              return (
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4 space-y-3" data-testid="campaigns-totals">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-emerald-300/70 uppercase tracking-wider text-[10px] font-semibold">Showing</span>
                      <span className="text-white text-xs font-semibold" data-testid="totals-scope-label">{scopeLabel}</span>
                      <span className="text-neutral-500 text-xs">· {scoped.length} campaign{scoped.length === 1 ? '' : 's'}</span>
                    </div>
                    <select
                      value={statsScope}
                      onChange={(e) => setStatsScope(e.target.value)}
                      className="bg-black border border-emerald-500/30 text-emerald-200 text-xs rounded-md px-2 py-1 focus:outline-none focus:border-emerald-400"
                      data-testid="totals-scope-select"
                    >
                      <option value="active">Active campaigns only</option>
                      <option value="completed">Completed campaigns</option>
                      <option value="all">All time (lifetime)</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <p className="text-emerald-300/70 uppercase tracking-wider text-[10px] font-semibold">Emails sent</p>
                      <p className="text-white font-bold text-xl mt-1" data-testid="totals-sent">{totals.sent.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-emerald-300/70 uppercase tracking-wider text-[10px] font-semibold">Total clicks</p>
                      <p className="text-emerald-400 font-bold text-xl mt-1" data-testid="totals-clicks">{totals.clicks.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-emerald-300/70 uppercase tracking-wider text-[10px] font-semibold">CTR</p>
                      <p className="text-white font-bold text-xl mt-1" data-testid="totals-ctr">{ctr}%</p>
                    </div>
                    <div>
                      <p className="text-emerald-300/70 uppercase tracking-wider text-[10px] font-semibold">Responses</p>
                      <p className="text-white font-bold text-xl mt-1" data-testid="totals-responded">{totals.responded.toLocaleString()}</p>
                    </div>
                  </div>
                </div>
              );
            })()}
            {/* Cohort autopilot health */}
            <div className="rounded-xl border border-white/10 bg-neutral-900/60 p-4 space-y-3" data-testid="cohort-health-panel">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <h2 className="text-sm font-bold text-white uppercase tracking-wide">Cohort autopilot health</h2>
                  <p className="text-xs text-neutral-500 mt-0.5">Leads that exist in cohorts but aren&rsquo;t queued for the autopilot. Resume any with a click.</p>
                </div>
                <button
                  onClick={loadDiagnostic}
                  className="text-xs text-[#00F0FF] hover:underline"
                  data-testid="diagnostic-refresh"
                >
                  Refresh
                </button>
              </div>
              {diagnosticLoading ? (
                <div className="text-xs text-neutral-400 flex items-center gap-2 py-3">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking cohort health…
                </div>
              ) : diagnostic.length === 0 ? (
                <p className="text-xs text-neutral-500 py-2">No cohorts found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs" data-testid="diagnostic-table">
                    <thead className="text-neutral-500 uppercase tracking-wide">
                      <tr className="border-b border-white/5">
                        <th className="text-left py-2 pr-3 font-semibold">Cohort</th>
                        <th className="text-right py-2 px-2 font-semibold">Total</th>
                        <th className="text-right py-2 px-2 font-semibold text-emerald-400">Sent</th>
                        <th className="text-right py-2 px-2 font-semibold text-[#00F0FF]">Queued</th>
                        <th className="text-right py-2 px-2 font-semibold text-amber-400">Stuck</th>
                        <th className="text-right py-2 pl-2 font-semibold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diagnostic.map((c) => (
                        <tr key={c.cohort_id} className="border-b border-white/5" data-testid={`diagnostic-row-${c.cohort_id}`}>
                          <td className="py-2.5 pr-3">
                            <div className="font-semibold text-white truncate max-w-[260px]">{c.name}</div>
                            <div className="text-[10px] text-neutral-500 font-mono truncate max-w-[260px]">{c.cohort_id}</div>
                          </td>
                          <td className="text-right py-2.5 px-2 text-neutral-300 font-mono">{c.total_with_email}</td>
                          <td className="text-right py-2.5 px-2 text-emerald-400 font-mono">{c.sent}</td>
                          <td className="text-right py-2.5 px-2 text-[#00F0FF] font-mono">{c.queued_for_autopilot}</td>
                          <td className="text-right py-2.5 px-2 text-amber-400 font-mono font-bold">{c.stuck_no_campaign}</td>
                          <td className="text-right py-2.5 pl-2">
                            <div className="flex items-center justify-end gap-2">
                              {c.needs_resume ? (
                                <button
                                  onClick={() => oneClickResume(c)}
                                  disabled={campaignActionId === c.cohort_id}
                                  className="px-2.5 py-1 text-[11px] font-semibold rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 disabled:opacity-50 disabled:cursor-wait"
                                  data-testid={`resume-cohort-${c.cohort_id}`}
                                >
                                  {campaignActionId === c.cohort_id ? (
                                    <><Loader2 className="w-3 h-3 inline-block mr-1 -mt-0.5 animate-spin" /> Resuming…</>
                                  ) : (
                                    <><Play className="w-3 h-3 inline-block mr-1 -mt-0.5" /> Resume autopilot</>
                                  )}
                                </button>
                              ) : (
                                <span className="text-[10px] text-neutral-600">healthy</span>
                              )}
                              <button
                                onClick={() => { setMarkModal(c); setMarkEmails(''); }}
                                className="text-[10px] text-neutral-400 hover:text-[#00F0FF] underline-offset-2 hover:underline"
                                data-testid={`mark-contacted-${c.cohort_id}`}
                                title="Mark a list of emails as already contacted so they're skipped"
                              >
                                Mark contacted
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">Email Campaigns</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowWebhookLog(true)}
                  className="text-xs text-neutral-400 hover:text-[#00F0FF] underline-offset-2 hover:underline"
                  data-testid="show-webhook-log"
                  title="Recent Resend webhook events — useful for debugging 'I clicked but it didn't register'"
                >
                  Webhook log
                </button>
                <button
                  onClick={loadCampaigns}
                  className="text-xs text-[#00F0FF] hover:underline"
                  data-testid="campaigns-refresh"
                >
                  Refresh
                </button>
              </div>
            </div>
            {campaignsLoading ? (
              <div className="text-sm text-neutral-400 flex items-center gap-2 py-6">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading campaigns…
              </div>
            ) : campaignsList.length === 0 ? (
              <div className="rounded-xl border border-white/5 bg-neutral-900/50 p-8 text-center">
                <Inbox className="w-8 h-8 text-neutral-600 mx-auto mb-3" />
                <p className="text-sm text-neutral-400">No campaigns yet.</p>
                <p className="text-xs text-neutral-500 mt-1">
                  Open a cohort under <strong>Search & Cohorts</strong> and hit "Launch campaign".
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {campaignsList.map((c) => {
                  const totalRows = (c.eligible_count || 0);
                  const sent = c.total_sent || 0;
                  const queued = c.total_queued || 0;
                  const pct = totalRows ? Math.round((sent / totalRows) * 100) : 0;
                  const statusBadge =
                    c.status === 'paused'
                      ? 'bg-yellow-500/20 text-yellow-300'
                      : sent >= totalRows && totalRows > 0
                        ? 'bg-emerald-500/20 text-emerald-300'
                        : 'bg-[#00F0FF]/20 text-[#00F0FF]';
                  const fmt = (iso) => {
                    if (!iso) return '—';
                    try {
                      return new Date(iso).toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: 'numeric', minute: '2-digit',
                      });
                    } catch { return iso; }
                  };
                  return (
                    <div
                      key={c.campaign_id}
                      className="rounded-xl border border-white/10 bg-neutral-900/50 p-4"
                      data-testid={`campaign-row-${c.campaign_id}`}
                    >
                      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`text-[10px] uppercase tracking-wide px-2 py-0.5 rounded-full font-semibold ${statusBadge}`}>
                              {c.status === 'paused' ? 'Paused' : sent >= totalRows && totalRows > 0 ? 'Complete' : 'Active'}
                            </span>
                            <span className="text-xs text-neutral-500 font-mono">{c.campaign_id}</span>
                          </div>
                          <p className="text-sm text-neutral-200 truncate max-w-xl">
                            <span className="text-neutral-500">A:</span> {c.subject_a}
                          </p>
                          {c.subject_b && (
                            <p className="text-sm text-neutral-200 truncate max-w-xl">
                              <span className="text-neutral-500">B:</span> {c.subject_b}
                            </p>
                          )}
                          <p className="text-xs text-neutral-500 mt-1">
                            Created {fmt(c.created_at)} · {c.daily_cap}/day cap · {c.estimated_days} days estimate
                          </p>
                          {/* Test-click verifier — only shows after the founder
                              actually clicks a link in a "Test send to me"
                              email. Confirms click→webhook→DB pipeline works
                              end-to-end without polluting per-business stats. */}
                          {c.last_test_clicked_at && (
                            <p
                              className="text-xs text-emerald-400 mt-1 flex items-center gap-1.5"
                              data-testid={`test-click-${c.campaign_id}`}
                              title={`${c.last_test_clicked_count || 1} test click(s) received`}
                            >
                              <MousePointerClick className="w-3 h-3" />
                              Test click received {fmt(c.last_test_clicked_at)}
                              {(c.last_test_clicked_count || 0) > 1 && (
                                <span className="text-emerald-500/80">
                                  ({c.last_test_clicked_count})
                                </span>
                              )}
                            </p>
                          )}
                          {!c.last_test_clicked_at && c.last_test_delivered_at && (
                            <p className="text-xs text-neutral-500 mt-1 flex items-center gap-1.5">
                              <Send className="w-3 h-3" />
                              Test delivered {fmt(c.last_test_delivered_at)} · waiting for your click
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEditCampaign(c)}
                            disabled={campaignActionId === c.campaign_id}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#00F0FF]/20 hover:bg-[#00F0FF]/30 text-[#00F0FF] border border-[#00F0FF]/40 disabled:opacity-50"
                            data-testid={`edit-campaign-${c.campaign_id}`}
                            title="Edit subject lines, body template, and daily cap. Applies to unsent recipients."
                          >
                            <Pencil className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                            Edit
                          </button>
                          <button
                            onClick={() => testSendCampaign(c.campaign_id)}
                            disabled={campaignActionId === c.campaign_id}
                            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#FF8C00]/20 hover:bg-[#FF8C00]/30 text-[#FF8C00] border border-[#FF8C00]/40 disabled:opacity-50"
                            data-testid={`test-send-${c.campaign_id}`}
                            title="Preview variant A in your own inbox before blasting the cohort"
                          >
                            <Send className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                            Test send to me
                          </button>
                          {c.status === 'paused' ? (
                            <button
                              onClick={() => resumeCampaign(c.campaign_id)}
                              disabled={campaignActionId === c.campaign_id}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 disabled:opacity-50"
                              data-testid={`resume-${c.campaign_id}`}
                            >
                              <Play className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                              Resume
                            </button>
                          ) : queued > 0 ? (
                            <button
                              onClick={() => pauseCampaign(c.campaign_id)}
                              disabled={campaignActionId === c.campaign_id}
                              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-300 border border-yellow-500/40 disabled:opacity-50"
                              data-testid={`pause-${c.campaign_id}`}
                            >
                              <Pause className="w-3 h-3 inline-block mr-1 -mt-0.5" />
                              Pause
                            </button>
                          ) : null}
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                        <div className="bg-black/40 rounded-lg p-2">
                          <p className="text-neutral-500">Progress</p>
                          <p className="text-white font-bold mt-0.5">{sent} / {totalRows}</p>
                          <div className="h-1.5 bg-white/5 rounded-full mt-1.5 overflow-hidden">
                            <div className="h-full bg-[#00F0FF] transition-all" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                        <div className="bg-black/40 rounded-lg p-2">
                          <p className="text-neutral-500">Variant A</p>
                          <p className="text-white font-bold mt-0.5">
                            {(c.stats?.a?.sent || 0) + (c.stats?.a?.responded || 0)}<span className="text-neutral-500 font-normal text-[10px]"> / {c.variant_a_count}</span>
                          </p>
                          <p className={`text-[10px] mt-0.5 ${(c.stats?.a?.clicks || 0) > 0 ? 'text-emerald-400' : 'text-neutral-600'}`} data-testid={`a-clicks-${c.campaign_id}`}>
                            {c.stats?.a?.clicks || 0} {(c.stats?.a?.clicks || 0) === 1 ? 'click' : 'clicks'}
                            {((c.stats?.a?.sent || 0) + (c.stats?.a?.responded || 0)) > 0 && (
                              <span className="text-neutral-500"> · {Math.round(((c.stats?.a?.clicks || 0) / ((c.stats?.a?.sent || 0) + (c.stats?.a?.responded || 0))) * 100)}% CTR</span>
                            )}
                          </p>
                        </div>
                        <div className="bg-black/40 rounded-lg p-2">
                          <p className="text-neutral-500">Variant B</p>
                          <p className="text-white font-bold mt-0.5">
                            {(c.stats?.b?.sent || 0) + (c.stats?.b?.responded || 0)}<span className="text-neutral-500 font-normal text-[10px]"> / {c.variant_b_count}</span>
                          </p>
                          <p className={`text-[10px] mt-0.5 ${(c.stats?.b?.clicks || 0) > 0 ? 'text-emerald-400' : 'text-neutral-600'}`} data-testid={`b-clicks-${c.campaign_id}`}>
                            {c.stats?.b?.clicks || 0} {(c.stats?.b?.clicks || 0) === 1 ? 'click' : 'clicks'}
                            {((c.stats?.b?.sent || 0) + (c.stats?.b?.responded || 0)) > 0 && (
                              <span className="text-neutral-500"> · {Math.round(((c.stats?.b?.clicks || 0) / ((c.stats?.b?.sent || 0) + (c.stats?.b?.responded || 0))) * 100)}% CTR</span>
                            )}
                          </p>
                        </div>
                        <div className="bg-black/40 rounded-lg p-2">
                          <p className="text-neutral-500">Next send</p>
                          <p className="text-white font-medium mt-0.5 truncate" data-testid={`next-${c.campaign_id}`}>
                            {c.status === 'paused' ? '— (paused)' : fmt(c.next_send_at)}
                          </p>
                        </div>
                        <div className="bg-black/40 rounded-lg p-2">
                          <p className="text-neutral-500">Last scheduled</p>
                          <p className="text-white font-medium mt-0.5 truncate">{fmt(c.last_send_at)}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className="text-[11px] text-neutral-500 mt-2">
              Cron tick: every 30 min. Each campaign sends <code>daily_cap</code> emails/day spread across the 13:00–21:00 UTC window (06:00 AM–02:00 PM PT / 09:00 AM–05:00 PM ET — peak work hours both coasts).
              Times shown in your local timezone.
            </p>
          </section>
        )}

        {activeTab === 'search' && (<>
        {/* Search form */}
        <section className="bg-neutral-900/70 border border-[#00F0FF]/30 rounded-2xl p-5 md:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-2">
                ZIP codes (comma separated, max 10)
              </label>
              <Input
                value={zipsText}
                onChange={(e) => setZipsText(e.target.value)}
                placeholder="98371, 98372, 98373, 98375"
                className="bg-neutral-950 border-white/10 text-white"
                data-testid="lead-finder-zips-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-2">
                Industry
              </label>
              <select
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
                className="w-full bg-neutral-950 border border-white/10 text-white rounded-md px-3 py-2 text-sm"
                data-testid="lead-finder-industry-select"
              >
                {industries.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={runSearch}
              disabled={loading}
              className="bg-[#00F0FF] text-black hover:bg-[#00C2CC] font-semibold"
              data-testid="lead-finder-search-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Searching…</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Search</>
              )}
            </Button>
            <p className="text-xs text-neutral-500">
              Rate-limited to 8 searches/hour per admin (safety cap).
            </p>
          </div>

          {/* Cohort exclusion picker */}
          {cohorts.length > 0 && (
            <div className="border-t border-white/10 pt-4 mt-2">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-4 h-4 text-[#00F0FF]" />
                <p className="text-xs uppercase tracking-wide text-neutral-400">
                  Exclude businesses from saved cohorts ({excludeCohortIds.size} selected)
                </p>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="lead-finder-cohort-exclude-list">
                {cohorts.map((c) => {
                  const active = excludeCohortIds.has(c.cohort_id);
                  return (
                    <button
                      key={c.cohort_id}
                      type="button"
                      onClick={() => toggleExcludeCohort(c.cohort_id)}
                      className={`group inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition ${
                        active
                          ? 'bg-[#00F0FF]/20 border-[#00F0FF]/50 text-[#00F0FF]'
                          : 'bg-neutral-950/50 border-white/10 text-neutral-300 hover:border-white/30'
                      }`}
                      data-testid={`lead-finder-cohort-pill-${c.cohort_id}`}
                    >
                      <span>{c.name}</span>
                      <span className="text-neutral-500">({c.business_count})</span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCohortDetails(c.cohort_id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            openCohortDetails(c.cohort_id);
                          }
                        }}
                        className="ml-1 text-neutral-400 hover:text-[#00F0FF] opacity-70 hover:opacity-100"
                        title="View details / update statuses"
                        data-testid={`lead-finder-cohort-view-${c.cohort_id}`}
                      >
                        <Eye className="w-3 h-3" />
                      </span>
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteCohort(c.cohort_id, c.name);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.stopPropagation();
                            deleteCohort(c.cohort_id, c.name);
                          }
                        }}
                        className="ml-1 text-red-400 hover:text-red-300 opacity-60 hover:opacity-100"
                        title="Delete cohort"
                      >
                        <Trash2 className="w-3 h-3" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* Result summary */}
        {meta && (
          <section className="bg-neutral-900/50 border border-white/10 rounded-xl p-4">
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="w-4 h-4" />
                <strong>{meta.total}</strong> businesses found
              </div>
              <span className="text-neutral-600">·</span>
              {Object.entries(meta.counts_by_zip).map(([z, c]) => (
                <div key={z} className="text-xs px-2 py-1 rounded-md bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF]">
                  ZIP {z}: <strong>{c}</strong>
                </div>
              ))}
              {meta.zip_codes_unresolved.length > 0 && (
                <div className="flex items-center gap-1 text-yellow-400 text-xs">
                  <AlertCircle className="w-4 h-4" />
                  Unresolved ZIPs: {meta.zip_codes_unresolved.join(', ')}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Export controls */}
        {results.length > 0 && (
          <section className="bg-neutral-900/70 border border-white/10 rounded-2xl p-4 flex flex-wrap gap-3 items-center justify-between">
            <div className="text-sm text-neutral-300">
              <strong>{selected.size}</strong> of {results.length} selected
              <button
                onClick={toggleAll}
                className="ml-3 text-[#00F0FF] hover:underline text-xs"
                data-testid="lead-finder-toggle-all"
              >
                {selected.size === results.length ? 'Deselect all' : 'Select all'}
              </button>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => downloadExport('combined')}
                disabled={exporting !== null || selected.size === 0}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="lead-finder-export-combined-btn"
              >
                {exporting === 'combined' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
                ) : (
                  <><FileSpreadsheet className="w-4 h-4 mr-2" /> Export as 1 .xlsx</>
                )}
              </Button>
              <Button
                onClick={() => downloadExport('by_zip')}
                disabled={exporting !== null || selected.size === 0}
                className="bg-[#00F0FF] text-black hover:bg-[#00C2CC] font-semibold"
                data-testid="lead-finder-export-by-zip-btn"
              >
                {exporting === 'by_zip' ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building zip…</>
                ) : (
                  <><Download className="w-4 h-4 mr-2" /> Export split by ZIP (.zip)</>
                )}
              </Button>
            </div>
          </section>
        )}

        {/* Save selection as cohort */}
        {results.length > 0 && (
          <section
            className="bg-neutral-900/70 border border-[#00F0FF]/20 rounded-2xl p-4 md:p-5"
            data-testid="lead-finder-save-cohort-section"
          >
            <div className="flex items-center gap-2 mb-3">
              <Save className="w-4 h-4 text-[#00F0FF]" />
              <h3 className="text-sm font-semibold text-white">Save selection as cohort</h3>
              <span className="text-xs text-neutral-500">
                — track who you've mailed so they don't reappear in future searches
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
              <Input
                value={cohortName}
                onChange={(e) => setCohortName(e.target.value)}
                placeholder="Cohort name (e.g. Puyallup SMB — April 2026)"
                className="bg-neutral-950 border-white/10 text-white md:col-span-2"
                data-testid="lead-finder-cohort-name-input"
              />
              <Input
                value={cohortNotes}
                onChange={(e) => setCohortNotes(e.target.value)}
                placeholder="Notes (optional)"
                className="bg-neutral-950 border-white/10 text-white"
                data-testid="lead-finder-cohort-notes-input"
              />
            </div>
            <Button
              onClick={saveAsCohort}
              disabled={savingCohort || selected.size === 0 || !cohortName.trim()}
              className="bg-[#00F0FF] text-black hover:bg-[#00C2CC] font-semibold"
              data-testid="lead-finder-save-cohort-btn"
            >
              {savingCohort ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</>
              ) : (
                <><Save className="w-4 h-4 mr-2" /> Save {selected.size} as cohort</>
              )}
            </Button>
          </section>
        )}

        {/* Results table */}
        {results.length > 0 && (
          <section className="bg-neutral-900/40 border border-white/10 rounded-xl overflow-hidden">
            <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
              <table className="w-full text-sm" data-testid="lead-finder-results-table">
                <thead className="bg-neutral-950 text-neutral-400 text-xs uppercase tracking-wide sticky top-0">
                  <tr>
                    <th className="px-3 py-3 text-left w-8"></th>
                    <th className="px-3 py-3 text-left">Business</th>
                    <th className="px-3 py-3 text-left">Address</th>
                    <th className="px-3 py-3 text-left">ZIP</th>
                    <th className="px-3 py-3 text-left">Phone</th>
                    <th className="px-3 py-3 text-left">Category</th>
                    <th className="px-3 py-3 text-right">★</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {results.map((b) => (
                    <tr
                      key={b.place_id}
                      className="hover:bg-white/5"
                      data-testid={`lead-row-${b.place_id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          checked={selected.has(b.place_id)}
                          onChange={() => toggleSelected(b.place_id)}
                          className="accent-[#00F0FF]"
                        />
                      </td>
                      <td className="px-3 py-2 text-white font-medium">{b.name}</td>
                      <td className="px-3 py-2 text-neutral-400 text-xs">{b.address}</td>
                      <td className="px-3 py-2 text-neutral-300 text-xs">{b.zip || '—'}</td>
                      <td className="px-3 py-2 text-neutral-300 text-xs whitespace-nowrap">
                        {b.phone || '—'}
                      </td>
                      <td className="px-3 py-2 text-neutral-400 text-xs">{b.category || '—'}</td>
                      <td className="px-3 py-2 text-right text-neutral-300 text-xs">
                        {b.rating ? `${b.rating} (${b.review_count || 0})` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {results.length === 0 && !loading && (
          <div className="text-center text-neutral-500 text-sm py-12 border border-dashed border-white/10 rounded-xl">
            Enter ZIP codes above and hit Search to find businesses.
          </div>
        )}
        </>)}
      </div>

      {/* Cohort details modal — mini-CRM for tracking mail status */}
      {(openCohort || openCohortLoading) && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          onClick={() => !openCohortLoading && setOpenCohort(null)}
          data-testid="lead-finder-cohort-modal"
        >
          <div
            className="bg-neutral-900 border border-white/10 rounded-2xl w-full max-w-5xl my-12 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {openCohortLoading ? (
              <div className="text-center text-neutral-400 py-12">
                <Loader2 className="w-6 h-6 mx-auto animate-spin mb-3" />
                Loading cohort…
              </div>
            ) : openCohort ? (
              <>
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{openCohort.name}</h3>
                    <p className="text-xs text-neutral-500 mt-1">
                      {openCohort.business_count} businesses · ZIPs {(openCohort.zip_codes || []).join(', ') || '—'}
                      {openCohort.notes ? ` · ${openCohort.notes}` : ''}
                    </p>
                    {openCohort.last_enriched_at && (
                      <p className="text-[11px] text-neutral-600 mt-1">
                        Last enriched: {openCohort.last_enriched_found || 0} emails found from {openCohort.last_enriched_attempted || 0} websites
                      </p>
                    )}
                    {openCohort.status_counts && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {Object.entries(openCohort.status_counts).map(([k, v]) => {
                          const meta = MAIL_STATUSES.find((m) => m.value === k);
                          return (
                            <span
                              key={k}
                              className={`text-[11px] px-2 py-0.5 rounded-full ${meta?.color || 'bg-neutral-800 text-neutral-300'}`}
                            >
                              {meta?.label || k}: <strong>{v}</strong>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => enrichEmails(openCohort.cohort_id)}
                      disabled={enrichingCohortId === openCohort.cohort_id}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#00F0FF]/15 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/25 disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Scrape websites for public contact emails. 35-50% hit rate, 2-3 min per 200 businesses."
                      data-testid="lead-finder-enrich-emails-btn"
                    >
                      {enrichingCohortId === openCohort.cohort_id ? (
                        <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enriching...</>
                      ) : (
                        <><Mail className="w-3.5 h-3.5" /> Enrich Emails</>
                      )}
                    </button>
                    <button
                      onClick={() => openCampaignModal(openCohort.cohort_id)}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25"
                      title="Compose + launch a throttled A/B email campaign on this cohort. Compliance footer auto-injected."
                      data-testid="lead-finder-send-campaign-btn"
                    >
                      <Send className="w-3.5 h-3.5" /> Send Campaign
                    </button>
                    <button
                      onClick={() => setOpenCohort(null)}
                      className="text-neutral-400 hover:text-white"
                      data-testid="lead-finder-cohort-modal-close"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto border border-white/5 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-950 text-neutral-400 text-xs uppercase tracking-wide sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Business</th>
                        <th className="px-3 py-2 text-left">Address</th>
                        <th className="px-3 py-2 text-left">Phone</th>
                        <th className="px-3 py-2 text-left">Email</th>
                        <th className="px-3 py-2 text-left w-44">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5">
                      {(openCohort.businesses || []).map((b) => (
                        <tr key={b.place_id} className="hover:bg-white/5">
                          <td className="px-3 py-2 text-white font-medium">{b.name}</td>
                          <td className="px-3 py-2 text-neutral-400 text-xs">{b.address}</td>
                          <td className="px-3 py-2 text-neutral-300 text-xs whitespace-nowrap">
                            {b.phone || '—'}
                          </td>
                          <td className="px-3 py-2 text-neutral-300 text-xs whitespace-nowrap">
                            {b.email ? (
                              <a href={`mailto:${b.email}`} className="text-[#00F0FF] hover:underline">{b.email}</a>
                            ) : (
                              <span className="text-neutral-600">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            <select
                              value={b.mail_status || 'queued'}
                              onChange={(e) =>
                                updateBusinessStatus(openCohort.cohort_id, b.place_id, e.target.value)
                              }
                              className="w-full bg-neutral-950 border border-white/10 text-white rounded px-2 py-1 text-xs"
                              data-testid={`lead-finder-cohort-status-${b.place_id}`}
                            >
                              {MAIL_STATUSES.map((s) => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        </div>
      )}

      {/* Campaign composer modal */}
      {campaignCohortId && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
          onClick={() => !campaignSending && closeCampaignModal()}
          data-testid="lead-finder-campaign-modal"
        >
          <div
            className="bg-neutral-900 border border-emerald-500/40 rounded-2xl w-full max-w-3xl my-8 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Send className="w-5 h-5 text-emerald-400" /> Compose Campaign
                </h3>
                <p className="text-xs text-neutral-500 mt-1">
                  Merge tokens: <code className="text-neutral-300">{'{{business_name}}'}</code>{' '}
                  <code className="text-neutral-300">{'{{city}}'}</code>{' '}
                  <code className="text-neutral-300">{'{{state}}'}</code>{' '}
                  <code className="text-neutral-300">{'{{zip}}'}</code>. Address +
                  unsubscribe link are auto-injected.
                </p>
              </div>
              <button onClick={closeCampaignModal} className="text-neutral-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Subject A</label>
                <Input
                  value={campaign.subject_a}
                  onChange={(e) => setCampaign({ ...campaign, subject_a: e.target.value })}
                  className="bg-neutral-950 border-white/10 text-white"
                  data-testid="campaign-subject-a"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">
                  Subject B (optional — leave blank to skip A/B split)
                </label>
                <Input
                  value={campaign.subject_b}
                  onChange={(e) => setCampaign({ ...campaign, subject_b: e.target.value })}
                  className="bg-neutral-950 border-white/10 text-white"
                  data-testid="campaign-subject-b"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Body</label>
                <textarea
                  value={campaign.body_template}
                  onChange={(e) => setCampaign({ ...campaign, body_template: e.target.value })}
                  rows={14}
                  className="w-full bg-neutral-950 border border-white/10 text-white text-sm rounded-md px-3 py-2 font-mono"
                  data-testid="campaign-body"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">From name</label>
                  <Input
                    value={campaign.from_name}
                    onChange={(e) => setCampaign({ ...campaign, from_name: e.target.value })}
                    className="bg-neutral-950 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Reply-To (optional)</label>
                  <Input
                    value={campaign.reply_to}
                    onChange={(e) => setCampaign({ ...campaign, reply_to: e.target.value })}
                    placeholder="e.g. partnerships@theailedger.ai (leave blank to use From)"
                    className="bg-neutral-950 border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-neutral-400 mb-1">Daily cap</label>
                  <Input
                    type="number"
                    min={1}
                    max={80}
                    value={campaign.daily_cap}
                    onChange={(e) => setCampaign({ ...campaign, daily_cap: parseInt(e.target.value) || 25 })}
                    className="bg-neutral-950 border-white/10 text-white"
                  />
                </div>
              </div>

              {campaignPreview && (
                <div className="bg-neutral-950 border border-white/10 rounded-lg p-4 text-sm space-y-3">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-neutral-500">Sample: {campaignPreview.sample_business?.name}</div>
                    <div className="text-xs text-neutral-400">{campaignPreview.sample_business?.email}</div>
                  </div>
                  <div>
                    <div className="text-xs text-neutral-500">Subject A:</div>
                    <div className="text-white font-medium">{campaignPreview.subject_a_rendered}</div>
                  </div>
                  {campaignPreview.subject_b_rendered && (
                    <div>
                      <div className="text-xs text-neutral-500">Subject B:</div>
                      <div className="text-white font-medium">{campaignPreview.subject_b_rendered}</div>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-neutral-500 mb-1">Body:</div>
                    <pre className="text-neutral-200 text-xs whitespace-pre-wrap font-sans">
                      {campaignPreview.body_rendered_text}
                    </pre>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-2">
                <Button
                  onClick={runPreview}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                  data-testid="campaign-preview-btn"
                >
                  <Eye className="w-4 h-4 mr-2" /> Preview merge
                </Button>
                <Button
                  onClick={launchCampaign}
                  disabled={campaignSending}
                  className="bg-emerald-500 text-black hover:bg-emerald-400 font-semibold"
                  data-testid="campaign-launch-btn"
                >
                  {campaignSending ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Launching...</>
                  ) : (
                    <><Send className="w-4 h-4 mr-2" /> Launch campaign</>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit campaign modal — subject/body/cap only. Cohort membership + variant assignment are immutable. */}
      {editCampaign && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" data-testid="edit-campaign-modal">
          <div className="bg-neutral-950 border border-[#00F0FF]/30 rounded-2xl max-w-2xl w-full my-12 shadow-2xl">
            <div className="p-6 border-b border-white/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Pencil className="w-4 h-4 text-[#00F0FF]" /> Edit campaign
                  </h3>
                  <p className="text-xs text-neutral-400 mt-1">
                    Campaign: <span className="text-white font-mono">{editCampaign.campaign_id}</span>
                  </p>
                  <p className="text-xs text-[#00F0FF] mt-2">
                    New copy applies to recipients still in the queue.
                    Already-sent emails are unchanged.
                  </p>
                </div>
                <button
                  onClick={() => setEditCampaign(null)}
                  className="text-neutral-500 hover:text-white text-xl leading-none"
                  data-testid="edit-modal-close"
                >&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Subject A *</label>
                <input
                  type="text"
                  value={editForm.subject_a}
                  onChange={(e) => setEditForm({ ...editForm, subject_a: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#00F0FF]/40 text-white text-sm"
                  placeholder="e.g. AI tools your {{city}} competitors are using"
                  data-testid="edit-subject-a"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Subject B (optional A/B variant)</label>
                <input
                  type="text"
                  value={editForm.subject_b}
                  onChange={(e) => setEditForm({ ...editForm, subject_b: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#00F0FF]/40 text-white text-sm"
                  placeholder="Leave blank to send only variant A"
                  data-testid="edit-subject-b"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Body template *</label>
                <textarea
                  rows={12}
                  value={editForm.body_template}
                  onChange={(e) => setEditForm({ ...editForm, body_template: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#00F0FF]/40 text-white text-sm font-mono"
                  placeholder="Use {{name}} and {{city}} for personalization."
                  data-testid="edit-body-template"
                />
                <p className="text-[10px] text-neutral-500 mt-1">
                  Placeholders auto-filled per business: <span className="text-neutral-300">{'{{name}}'}</span>, <span className="text-neutral-300">{'{{city}}'}</span>.
                </p>
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Daily send cap</label>
                <input
                  type="number"
                  min={1}
                  max={5000}
                  value={editForm.daily_cap}
                  onChange={(e) => setEditForm({ ...editForm, daily_cap: e.target.value })}
                  className="w-32 px-3 py-2 rounded-lg bg-black/50 border border-white/10 focus:border-[#00F0FF]/40 text-white text-sm"
                  data-testid="edit-daily-cap"
                />
              </div>
            </div>
            <div className="p-6 border-t border-white/5 flex justify-end gap-3">
              <button
                onClick={() => setEditCampaign(null)}
                className="px-4 py-2 text-sm rounded-lg bg-white/5 hover:bg-white/10 text-neutral-300"
                data-testid="edit-modal-cancel"
              >Cancel</button>
              <button
                onClick={submitEditCampaign}
                disabled={editSubmitting}
                className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#00F0FF] hover:bg-[#00F0FF]/80 text-black disabled:opacity-50"
                data-testid="edit-modal-save"
              >
                {editSubmitting ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume autopilot modal */}
      {resumeModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" data-testid="resume-modal">
          <div className="bg-neutral-950 border border-amber-500/30 rounded-2xl max-w-2xl w-full my-12 shadow-2xl">
            <div className="p-6 border-b border-white/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white">Resume autopilot</h3>
                  <p className="text-xs text-neutral-400 mt-1">
                    Cohort: <span className="text-white font-medium">{resumeModal.name}</span>
                  </p>
                  <p className="text-xs text-amber-300 mt-2">
                    {resumeModal.stuck_no_campaign} stuck leads will be queued · ~{Math.max(1, Math.ceil(resumeModal.stuck_no_campaign / (Number(resumeForm.daily_cap) || 25)))} day{Math.ceil(resumeModal.stuck_no_campaign / (Number(resumeForm.daily_cap) || 25)) === 1 ? '' : 's'} at {resumeForm.daily_cap}/day
                  </p>
                </div>
                <button
                  onClick={() => setResumeModal(null)}
                  className="text-neutral-500 hover:text-white text-xl leading-none"
                  data-testid="resume-modal-close"
                >&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Subject A *</label>
                <input
                  type="text"
                  value={resumeForm.subject_a}
                  onChange={(e) => setResumeForm(f => ({ ...f, subject_a: e.target.value }))}
                  placeholder="Quick question about {{business_name}}"
                  className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00F0FF] focus:outline-none"
                  data-testid="resume-subject-a"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Subject B (optional, A/B split 50/50)</label>
                <input
                  type="text"
                  value={resumeForm.subject_b}
                  onChange={(e) => setResumeForm(f => ({ ...f, subject_b: e.target.value }))}
                  placeholder="Saw your shop on Google — 30 seconds?"
                  className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00F0FF] focus:outline-none"
                  data-testid="resume-subject-b"
                />
              </div>
              <div>
                <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">
                  Body * <span className="text-neutral-600">— merge tokens: <code className="text-[#00F0FF]">{`{{business_name}}`}</code> <code className="text-[#00F0FF]">{`{{city}}`}</code></span>
                </label>
                <textarea
                  rows={9}
                  value={resumeForm.body_template}
                  onChange={(e) => setResumeForm(f => ({ ...f, body_template: e.target.value }))}
                  placeholder={`Hi {{business_name}} team,\n\nQuick one — I noticed you're in {{city}} and...`}
                  className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00F0FF] focus:outline-none font-mono leading-relaxed"
                  data-testid="resume-body"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">Daily cap</label>
                  <input
                    type="number"
                    min="1" max="500"
                    value={resumeForm.daily_cap}
                    onChange={(e) => setResumeForm(f => ({ ...f, daily_cap: e.target.value }))}
                    className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00F0FF] focus:outline-none font-mono"
                    data-testid="resume-daily-cap"
                  />
                </div>
                <div>
                  <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block mb-1.5">From name</label>
                  <input
                    type="text"
                    value={resumeForm.from_name}
                    onChange={(e) => setResumeForm(f => ({ ...f, from_name: e.target.value }))}
                    className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00F0FF] focus:outline-none"
                    data-testid="resume-from-name"
                  />
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/5 flex justify-end gap-2">
              <Button
                onClick={() => setResumeModal(null)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="resume-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={submitResume}
                disabled={resumeSubmitting}
                className="bg-amber-500 text-black hover:bg-amber-400 font-semibold"
                data-testid="resume-submit"
              >
                {resumeSubmitting ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Resuming...</>
                ) : (
                  <><Play className="w-4 h-4 mr-2" /> Resume autopilot</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Mark-as-already-contacted modal */}
      {markModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto" data-testid="mark-contacted-modal">
          <div className="bg-neutral-950 border border-white/15 rounded-2xl max-w-xl w-full my-12 shadow-2xl">
            <div className="p-6 border-b border-white/5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold text-white">Mark already contacted</h3>
                  <p className="text-xs text-neutral-400 mt-1">
                    Cohort: <span className="text-white font-medium">{markModal.name}</span>
                  </p>
                  <p className="text-xs text-neutral-500 mt-2">
                    Paste the emails of leads in this cohort that were already contacted outside the autopilot.
                    They'll be marked <span className="text-emerald-400 font-mono">sent</span> and the cron will skip them.
                  </p>
                </div>
                <button onClick={() => setMarkModal(null)} className="text-neutral-500 hover:text-white text-xl leading-none" data-testid="mark-modal-close">&times;</button>
              </div>
            </div>
            <div className="p-6 space-y-3">
              <label className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400 block">
                Emails — one per line, or comma/space separated
              </label>
              <textarea
                rows={6}
                value={markEmails}
                onChange={(e) => setMarkEmails(e.target.value)}
                placeholder={'joes-coffee@example.com\nmaria@thai-spot.com\nhello@bakery-co.com'}
                className="w-full bg-neutral-900 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono focus:border-[#00F0FF] focus:outline-none"
                data-testid="mark-emails-input"
              />
            </div>
            <div className="p-6 border-t border-white/5 flex justify-end gap-2">
              <Button onClick={() => setMarkModal(null)} variant="outline" className="border-white/20 text-white hover:bg-white/10">Cancel</Button>
              <Button
                onClick={submitMarkContacted}
                disabled={markSubmitting}
                className="bg-[#00F0FF] text-black hover:bg-[#00D4E0] font-semibold"
                data-testid="mark-submit"
              >
                {markSubmitting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Marking...</>) : 'Mark as sent'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Resend webhook event log — diagnostic for "click didn't register" */}
      {showWebhookLog && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start justify-center p-6 overflow-y-auto"
          data-testid="webhook-log-modal"
        >
          <div className="bg-neutral-950 border border-white/15 rounded-2xl max-w-3xl w-full my-12 shadow-2xl">
            <div className="p-5 border-b border-white/5 flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-white">Recent Resend webhook events</h3>
                <p className="text-xs text-neutral-400 mt-1">
                  Every event Resend has fired at <code className="text-[10px] text-[#00F0FF]">/api/webhooks/resend</code>.
                  If a click never appears here, Resend never sent it — check your Resend domain&apos;s click-tracking toggle in resend.com → Domains → Settings.
                </p>
              </div>
              <button
                onClick={() => setShowWebhookLog(false)}
                className="text-neutral-500 hover:text-white text-xl leading-none"
                data-testid="webhook-log-close"
              >
                &times;
              </button>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex flex-wrap gap-2">
                  {webhookEvents?.type_counts && Object.entries(webhookEvents.type_counts).map(([k, v]) => (
                    <span
                      key={k}
                      className="text-[10px] px-2 py-1 rounded-full bg-neutral-900 border border-white/10 text-neutral-300 font-mono"
                    >
                      {k}: <span className="text-white font-bold">{v}</span>
                    </span>
                  ))}
                </div>
                <button
                  onClick={loadWebhookEvents}
                  className="text-xs text-[#00F0FF] hover:underline"
                  data-testid="webhook-log-refresh"
                >
                  Refresh
                </button>
              </div>
              {webhookLoading ? (
                <div className="text-center py-8 text-neutral-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" />
                  Loading events…
                </div>
              ) : webhookEvents?.events?.length ? (
                <div className="overflow-x-auto max-h-[60vh] overflow-y-auto rounded-lg border border-white/5">
                  <table className="w-full text-xs">
                    <thead className="bg-neutral-900 text-neutral-400 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">Received</th>
                        <th className="text-left px-3 py-2 font-semibold">Event</th>
                        <th className="text-left px-3 py-2 font-semibold">Campaign tag</th>
                        <th className="text-left px-3 py-2 font-semibold">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {webhookEvents.events.map((e, i) => (
                        <tr key={i} className="border-t border-white/5 hover:bg-neutral-900/40">
                          <td className="px-3 py-2 text-neutral-400 font-mono whitespace-nowrap">
                            {e.received_at ? new Date(e.received_at).toLocaleString() : '—'}
                          </td>
                          <td className="px-3 py-2">
                            <span className={`font-mono text-[11px] ${
                              e.event_type === 'email.clicked' ? 'text-emerald-400' :
                              e.event_type === 'email.opened' ? 'text-sky-400' :
                              e.event_type === 'email.delivered' ? 'text-neutral-300' :
                              e.event_type === 'email.bounced' || e.event_type === 'email.complained' ? 'text-orange-400' :
                              'text-neutral-500'
                            }`}>
                              {e.event_type || '—'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-neutral-300 font-mono text-[11px]">
                            {e.campaign || <span className="text-neutral-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-neutral-400 text-[11px]">
                            {e.to || <span className="text-neutral-600">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-neutral-500 text-sm">
                  No webhook events received yet.<br />
                  <span className="text-neutral-600 text-xs">
                    If you&apos;ve sent test emails and nothing shows here, Resend isn&apos;t
                    reaching this endpoint at all.
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </AdminLayout>
  );
}
